import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import NotificationRow from './NotificationRow';
import { useAuth } from '../context/AuthContext';
import { listInquiries } from '../api/inquiries';
import logoMark from '../assets/logo-mark.png';
import {
  Notification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notifications';
import { resolveNotificationPath } from '../api/notificationLinks';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  badge?: number;
}

const NAV: NavItem[] = [
  { id: 'dashboard',    label: 'Dashboard',  icon: 'layout-dashboard', path: '/agent' },
  { id: 'listings',     label: 'My Listings', icon: 'building-2',      path: '/agent/listings' },
  { id: 'add-listing',  label: 'Add Listing', icon: 'plus-circle',     path: '/agent/listings/new' },
  { id: 'inquiries',    label: 'Inquiries',  icon: 'message-square',   path: '/agent/inquiries' },
  // { id: 'maintenance',  label: 'Maintenance', icon: 'wrench',          path: '/agent/maintenance', badge: 2 },
  { id: 'subscription', label: 'Subscription', icon: 'credit-card',    path: '/agent/subscription' },
  // { id: 'settings',     label: 'Settings',   icon: 'settings',         path: '/agent/settings' },
];

const PAGE_META: Record<string, { title: string; subtitle: string }> = {
  '/agent':                  { title: 'Dashboard',     subtitle: 'Overview of your listings and activity' },
  '/agent/listings':         { title: 'My Listings',   subtitle: 'Manage your properties' },
  '/agent/listings/new':     { title: 'Add Listing',   subtitle: 'Create a new property listing' },
  '/agent/inquiries':        { title: 'Inquiries',     subtitle: 'Messages from interested customers' },
  '/agent/maintenance':      { title: 'Maintenance',   subtitle: 'Service requests for your properties' },
  '/agent/subscription':     { title: 'Subscription',  subtitle: 'Your plan and billing' },
  '/agent/settings':         { title: 'Settings',      subtitle: 'Profile and preferences' },
};

const initials = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const AgentLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const meta =
    PAGE_META[location.pathname] ??
    (/^\/agent\/listings\/\d+\/edit\/?$/.test(location.pathname)
      ? { title: 'Edit Listing', subtitle: 'Update your property listing' }
      : /^\/agent\/listings\/\d+\/?$/.test(location.pathname)
      ? { title: 'Listing Details', subtitle: 'View your property listing' }
      : { title: 'Merchant', subtitle: '' });

  // Live count of "new" inquiries - refreshes every time the user navigates
  // (so leaving the inquiries page after marking some contacted updates the
  // badge without a full reload).
  const [inquiryBadge, setInquiryBadge] = useState<number>(0);
  useEffect(() => {
    let cancelled = false;
    listInquiries({ limit: 1 })
      .then((res) => {
        if (!cancelled) setInquiryBadge(res.counts.new || 0);
      })
      .catch(() => {
        if (!cancelled) setInquiryBadge(0);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Notification bell: unread dot + a dropdown panel anchored below the bell.
  const [notifOpen, setNotifOpen] = useState<boolean>(false);
  const [notifLoading, setNotifLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const notifRef = useRef<HTMLDivElement | null>(null);

  // Close the panel on outside click or Escape.
  useEffect(() => {
    if (!notifOpen) return;
    const onDown = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNotifOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [notifOpen]);

  useEffect(() => {
    let cancelled = false;
    listNotifications({ limit: 1 })
      .then((res) => {
        if (!cancelled) setUnreadCount(res.unreadCount);
      })
      .catch(() => {
        if (!cancelled) setUnreadCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const openNotifications = async () => {
    if (notifOpen) {
      setNotifOpen(false);
      return;
    }
    setNotifOpen(true);
    setNotifLoading(true);
    try {
      const res = await listNotifications({ limit: 30 });
      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      /* leave state as-is; user can retry */
    }
  };

  /*
   * Open what a notification is about. The dropdown lives inside notifRef, so
   * the outside-click handler never fires for it — close it here or it stays
   * open over the destination page.
   */
  const handleNotificationClick = (n: Notification, path: string) => {
    setNotifOpen(false);
    if (!n.isRead) {
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      markNotificationRead(n.id).catch(() => {
        /* badge re-syncs on the next fetch */
      });
    }
    navigate(path);
  };

  const navItems = NAV.map((n) =>
    n.id === 'inquiries' ? { ...n, badge: inquiryBadge } : n
  );

  // NavLink's prefix matching can't express "My Listings owns /agent/listings/*
  // except /agent/listings/new, which is its own nav item" — so decide here.
  const isItemActive = (path: string): boolean => {
    const current = location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/agent') return current === '/agent';
    if (path === '/agent/listings') {
      return (
        current === '/agent/listings' ||
        (current.startsWith('/agent/listings/') &&
          current !== '/agent/listings/new')
      );
    }
    return current === path || current.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
    navigate('/agent/login', { replace: true });
  };

  // Below the lg breakpoint the sidebar is an off-canvas drawer.
  const [navOpen, setNavOpen] = useState<boolean>(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [navOpen]);

  return (
    <div className="min-h-screen" style={{ background: '#E8E6E6' }}>
      {/* Drawer backdrop (mobile/tablet only) */}
      {navOpen && (
        <div
          className="fixed inset-0 lg:hidden"
          style={{ zIndex: 90, background: 'rgba(0,0,0,.4)' }}
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: fixed on desktop, slide-in drawer below lg */}
      <aside
        className={
          'fixed inset-y-0 left-0 flex flex-col overflow-y-auto w-[264px] max-w-[85vw] transition-transform duration-200 ease-out lg:translate-x-0 ' +
          (navOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full')
        }
        style={{
          zIndex: 100,
          background: '#fff',
          borderRight: '1px solid var(--g200)',
        }}
        aria-label="Merchant navigation"
      >
        {/* Brand */}
        <div
          className="flex items-center gap-3 px-5 pt-5 pb-4"
          style={{ borderBottom: '1px solid var(--g100)' }}
        >
          <div
            className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
            onClick={() => navigate('/agent')}
          >
            <img
              src={logoMark}
              alt="Rentagade"
              className="rounded-full object-contain flex-shrink-0"
              style={{ width: 42, height: 42 }}
            />
            <div>
              <div
                className="font-extrabold text-ink leading-tight"
                style={{ fontSize: 17, letterSpacing: '-0.4px' }}
              >
                Rentagade
              </div>
              <div
                className="font-semibold uppercase text-g400"
                style={{ fontSize: 10.5, letterSpacing: '1.2px' }}
              >
                Merchant Panel
              </div>
            </div>
          </div>
          <button
            type="button"
            className="lg:hidden rounded-[10px] flex items-center justify-center hover:bg-g100 flex-shrink-0"
            style={{ width: 36, height: 36 }}
            onClick={() => setNavOpen(false)}
            aria-label="Close menu"
          >
            <Icon name="x" size={20} color="var(--g600)" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-2.5">
          {navItems.map((n) => {
            const isActive = isItemActive(n.path);
            return (
              <NavLink
                key={n.id}
                to={n.path}
                className={
                  'flex items-center gap-3 px-3.5 py-2.5 rounded-[10px] text-sm transition-all w-full text-left' +
                  (isActive ? ' font-semibold' : ' font-medium hover:bg-g50')
                }
                style={{
                  background: isActive ? 'var(--brand-light)' : 'transparent',
                  color: isActive ? 'var(--brand)' : 'var(--g600)',
                  marginBottom: 2,
                }}
              >
                <Icon
                  name={n.icon}
                  size={20}
                  color={isActive ? 'var(--brand)' : 'currentColor'}
                />
                <span style={{ flex: 1 }}>{n.label}</span>
                {n.badge !== undefined && n.badge > 0 && (
                  <span
                    className="text-white font-bold"
                    style={{
                      background: 'var(--brand)',
                      fontSize: 10.5,
                      padding: '2px 8px',
                      borderRadius: 100,
                      lineHeight: 1.4,
                    }}
                  >
                    {n.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer user card */}
        <div
          className="flex items-center gap-2.5 px-4 pt-3.5 pb-4"
          style={{ borderTop: '1px solid var(--g100)' }}
        >
          <div
            className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
            style={{
              width: 36,
              height: 36,
              background: 'var(--brand-bg)',
              color: 'var(--brand)',
              fontSize: 13,
            }}
          >
            {user ? initials(user.name) : 'AG'}
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="font-semibold text-ink truncate"
              style={{ fontSize: 13 }}
            >
              {user?.name || 'Merchant'}
            </div>
            <div className="text-g400" style={{ fontSize: 11 }}>
              Merchant Account
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-md p-1.5 hover:bg-g100 transition-colors"
            title="Sign out"
            aria-label="Sign out"
          >
            <Icon name="log-out" size={16} color="var(--g400)" />
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="lg:ml-[264px] min-h-screen">
        {/* Header */}
        <header
          className="flex justify-between items-center gap-3 px-4 py-3 sm:px-6 lg:px-8 lg:py-[18px]"
          style={{
            background: '#fff',
            borderBottom: '1px solid var(--g100)',
            position: 'sticky',
            top: 0,
            zIndex: 50,
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden rounded-[10px] flex items-center justify-center flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                border: '1px solid var(--g200)',
                background: '#fff',
              }}
              onClick={() => setNavOpen(true)}
              aria-label="Open menu"
              aria-expanded={navOpen}
            >
              <Icon name="menu" size={20} color="var(--g600)" />
            </button>
            <div className="min-w-0">
              <h1
                className="font-extrabold text-ink truncate text-lg lg:text-[21px]"
                style={{ letterSpacing: '-0.5px' }}
              >
                {meta.title}
              </h1>
              {meta.subtitle && (
                <p
                  className="text-g400 font-normal hidden sm:block truncate"
                  style={{ fontSize: 13, marginTop: 2 }}
                >
                  {meta.subtitle}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                className="rounded-[10px] flex items-center justify-center relative"
                style={{
                  width: 40,
                  height: 40,
                  border: '1px solid var(--g200)',
                  background: '#fff',
                  cursor: 'pointer',
                }}
                aria-label="Notifications"
                onClick={openNotifications}
              >
                <Icon name="bell" size={18} color="var(--g600)" />
                {unreadCount > 0 && (
                  <span
                    className="absolute rounded-full"
                    style={{
                      top: 8,
                      right: 8,
                      width: 7,
                      height: 7,
                      background: 'var(--brand)',
                      border: '2px solid #fff',
                    }}
                  />
                )}
              </button>

              {/* Dropdown panel anchored below the bell */}
              {notifOpen && (
                <div
                  role="dialog"
                  aria-label="Notifications"
                  style={{
                    position: 'absolute',
                    top: 48,
                    right: 0,
                    width: 380,
                    maxWidth: 'calc(100vw - 32px)',
                    background: '#fff',
                    borderRadius: 16,
                    border: '1px solid var(--g100)',
                    boxShadow: '0 16px 40px rgba(0,0,0,.14)',
                    zIndex: 200,
                    overflow: 'hidden',
                    animation: 'fadeIn .15s ease both',
                  }}
                >
                  <div
                    className="flex justify-between items-center"
                    style={{
                      padding: '14px 18px',
                      borderBottom: '1px solid var(--g100)',
                    }}
                  >
                    <span className="font-bold text-ink" style={{ fontSize: 14.5 }}>
                      Notifications
                    </span>
                    {unreadCount > 0 && !notifLoading && (
                      <button
                        onClick={handleMarkAllRead}
                        className="font-semibold hover:underline"
                        style={{ fontSize: 12, color: 'var(--brand)' }}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: 380, overflowY: 'auto', padding: '4px 18px' }}>
                    {notifLoading ? (
                      <div
                        className="py-8 text-center text-g500"
                        style={{ fontSize: 13 }}
                      >
                        Loading notifications...
                      </div>
                    ) : notifications.length === 0 ? (
                      <div
                        className="flex flex-col items-center py-8 text-center"
                        style={{ color: 'var(--g500)' }}
                      >
                        <div
                          className="rounded-2xl flex items-center justify-center mb-3"
                          style={{
                            width: 48,
                            height: 48,
                            background: 'var(--g100)',
                          }}
                        >
                          <Icon name="bell" size={22} color="var(--g400)" />
                        </div>
                        <div
                          className="font-semibold text-ink"
                          style={{ fontSize: 13.5 }}
                        >
                          No notification found
                        </div>
                        <div style={{ fontSize: 12, marginTop: 3 }}>
                          You're all caught up. New activity will show up here.
                        </div>
                      </div>
                    ) : (
                      notifications.map((n, i) => (
                        <NotificationRow
                          key={n.id}
                          notification={n}
                          showSeparator={i > 0}
                          to={resolveNotificationPath(n, 'agent')}
                          onActivate={handleNotificationClick}
                        />
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page outlet */}
        <main
          className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-7"
          style={{ maxWidth: 1200 }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AgentLayout;
